// src/pages/TicketTypeManagement/index.tsx
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import BreadCrumb from "Components/Common/BreadCrumb";
import DataTableShell, { getSttNumber } from "Components/Common/DataTableShell";
import NumberInput from "Components/Common/NumberInput";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import { useBrandingTitle } from "../../hooks";
import { CacheService } from "services/cache";
import { CacheConfig } from "services/cache/CacheConfig";
import {
  fetchTicketTypes,
  createTicketType,
  updateTicketType,
  lockTicketType,
  unlockTicketType,
  deleteTicketType,
} from "slices/ticketType/thunk";
import {
  setPage,
  setFilters,
  clearFilters,
  openModal,
  closeModal,
  setSelectedTicketType,
} from "slices/ticketType/reducer";
import {
  CreateTicketTypeDto,
  LOCK_STATUS_OPTIONS,
  TicketType,
  UpdateTicketTypeDto,
} from "./types";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_type_management');

const initialCreateForm: CreateTicketTypeDto = {
  name: "",
  digits: 5,
  drawRounds: 18,
  quantityPerPeriod: 0,
  displayOrder: 0,
};

const TicketTypeManagementPage: React.FC = () => {
  useBrandingTitle(st('quan_ly_loai_ve_text'));

  const dispatch = useDispatch();
  const location = useLocation();
  const {
    ticketTypes,
    selectedTicketType,
    pagination,
    filters,
    loading,
    submitting,
    modalState,
  } = useSelector((state: any) => state.TicketType);

  const [createForm, setCreateForm] =
    useState<CreateTicketTypeDto>(initialCreateForm);
  const [editForm, setEditForm] = useState<UpdateTicketTypeDto | null>(null);
  const [searchValue, setSearchValue] = useState(filters.searchTerm || "");

  // Message hiển thị ngay dưới ô "Số lượng vé tạo mỗi kỳ" (mục UAT-CAT-007) — native "min=1" của
  // input number chỉ chặn submit bằng tooltip trình duyệt (không đồng nhất giao diện, không có
  // trong DOM để test), nên validate riêng ở đây để show message nhất quán với các ô khác.
  const [createQuantityError, setCreateQuantityError] = useState<string | null>(
    null,
  );
  // Xác nhận xóa loại vé — dùng state local, không qua redux modalState.
  const [pendingDelete, setPendingDelete] = useState<TicketType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editQuantityError, setEditQuantityError] = useState<string | null>(
    null,
  );

  // Tập action-code tĩnh của cột "Thao tác" — dùng để ẩn hẳn cột này khi role hiện tại không có
  // quyền với bất kỳ action nào trong cột.
  const TICKET_TYPE_ACTION_COLUMN_CODES = ["button-edit", "button-toggle-lock", "button-delete"];
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    TICKET_TYPE_ACTION_COLUMN_CODES,
  );

  const loadTicketTypes = () => {
    dispatch(
      fetchTicketTypes({
        pageIndex: pagination.currentPage,
        pageSize: pagination.pageSize,
        search: filters.searchTerm || undefined,
        isLocked:
          filters.isLocked === "All" ? undefined : filters.isLocked === "true",
      }) as any,
    );
  };

  useEffect(() => {
    loadTicketTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, filters]);

  // Debounce ô tìm kiếm — chỉ dispatch vào filters (và gọi API) sau 500ms ngừng gõ
  useEffect(() => {
    if (searchValue === filters.searchTerm) return;
    const timer = setTimeout(() => {
      dispatch(setFilters({ searchTerm: searchValue }));
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, dispatch]);

  // Đồng bộ lại state local khi filters bị xóa từ bên ngoài (nút "Xóa lọc")
  useEffect(() => {
    if (filters.searchTerm === "") {
      setSearchValue("");
    }
  }, [filters.searchTerm]);

  const handleOpenCreate = () => {
    setCreateForm(initialCreateForm);
    setCreateQuantityError(null);
    dispatch(openModal("create"));
  };

  const handleOpenEdit = (ticketType: TicketType) => {
    dispatch(setSelectedTicketType(ticketType));
    setEditForm({
      id: ticketType.id,
      name: ticketType.name,
      digits: ticketType.digits,
      drawRounds: ticketType.drawRounds,
      quantityPerPeriod: ticketType.quantityPerPeriod,
      displayOrder: ticketType.displayOrder,
    });
    setEditQuantityError(null);
    dispatch(openModal("edit"));
  };

  const handleCloseCreate = () => {
    if (submitting) return;
    dispatch(closeModal("create"));
  };

  const handleCloseEdit = () => {
    if (submitting) return;
    dispatch(closeModal("edit"));
    dispatch(setSelectedTicketType(null));
    setEditForm(null);
  };

  const handleCreateSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (createForm.quantityPerPeriod <= 0) {
      setCreateQuantityError(st('so_luong_ve_phat_hanh_mac_text'));
      return;
    }
    setCreateQuantityError(null);
    const result = await dispatch(createTicketType(createForm) as any);
    if (createTicketType.fulfilled.match(result)) {
      toast.success(st('tao_loai_ve_thanh_cong_message'));
      CacheService.invalidate(CacheConfig.ACTIVE_TICKET_TYPES.key);
      loadTicketTypes();
    } else {
      toast.error((result.payload as string) || st('khong_the_tao_loai_ve_message'));
    }
  };

  const handleEditSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editForm) return;
    if (editForm.quantityPerPeriod <= 0) {
      setEditQuantityError(st('so_luong_ve_phat_hanh_mac_text'));
      return;
    }
    setEditQuantityError(null);
    const result = await dispatch(updateTicketType(editForm) as any);
    if (updateTicketType.fulfilled.match(result)) {
      toast.success(st('cap_nhat_loai_ve_thanh_cong_message'));
      CacheService.invalidate(CacheConfig.ACTIVE_TICKET_TYPES.key);
      loadTicketTypes();
    } else {
      toast.error((result.payload as string) || st('khong_the_cap_nhat_loai_ve_message'));
    }
  };

  const handleToggleLock = async (ticketType: TicketType) => {
    const action = ticketType.isLocked ? unlockTicketType : lockTicketType;
    const result = await dispatch(action(ticketType.id) as any);
    if (action.fulfilled.match(result)) {
      toast.success(
        ticketType.isLocked ? st('da_mo_khoa_loai_ve_message') : st('da_khoa_loai_ve_message'),
      );
      CacheService.invalidate(CacheConfig.ACTIVE_TICKET_TYPES.key);
    } else {
      toast.error(
        (result.payload as string) || st('khong_the_thay_doi_trang_thai_message'),
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await dispatch(deleteTicketType(pendingDelete.id) as any);
    setDeleting(false);
    if (deleteTicketType.fulfilled.match(result)) {
      toast.success(st('da_xoa_loai_ve_name_message', { name: pendingDelete.name }));
      CacheService.invalidate(CacheConfig.ACTIVE_TICKET_TYPES.key);
      setPendingDelete(null);
      loadTicketTypes();
    } else {
      // Backend trả 409 kèm message chi tiết khi loại vé còn được dùng ở kế hoạch phát hành.
      toast.error((result.payload as string) || st('khong_the_xoa_loai_ve_message'));
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('quan_ly_loai_ve_text')} pageTitle={st('quan_ly_phat_hanh_text')} />

          <Card>
            <CardHeader className="card-header border-0">
              <Row className="align-items-center gy-3">
                <div className="col-sm">
                  <h5 className="card-title mb-0">{st('danh_sach_loai_ve_title')}</h5>
                </div>
                <div className="col-sm-auto">
                  <div className="d-flex gap-1 flex-wrap">
                    {PageActionPermission.canDisplay(
                      location.pathname,
                      "button-add",
                    ) && (
                      <Button
                        color="success"
                        data-action-code="button-add"
                        data-action-name="Thêm loại vé"
                        onClick={handleOpenCreate}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        {st('them_loai_ve_button')}
                      </Button>
                    )}
                    <Button
                      color="soft-secondary"
                      disabled={loading}
                      onClick={() => dispatch(clearFilters())}
                    >
                      <i className="ri-refresh-line align-bottom me-1"></i>
                      {st('xoa_loc_button')}
                    </Button>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="g-3 mb-3 align-items-end">
                <Col md={4}>
                  <Label className="form-label">{st('tim_kiem_label')}</Label>
                  <Input
                    type="text"
                    placeholder={st('ma_hoac_ten_loai_ve_placeholder')}
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('trang_thai_label')}</Label>
                  <Input
                    type="select"
                    value={filters.isLocked}
                    onChange={(event) =>
                      dispatch(
                        setFilters({ isLocked: event.target.value as any }),
                      )
                    }
                  >
                    {LOCK_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>

              <DataTableShell
                headerRow={
                  <tr>
                    <th className="text-center" style={{ width: 60 }}>
                      {st('stt_column')}
                    </th>
                    <th>{st('ma_loai_ve_column')}</th>
                    <th>{st('ten_loai_ve_column')}</th>
                    <th>{st('so_chu_so_dai_so_column')}</th>
                    <th>{st('so_lan_quay_thuong_column')}</th>
                    <th>{st('so_luong_ve_tao_moi_ky_column')}</th>
                    <th>{st('thu_tu_hien_thi_column')}</th>
                    <th>{st('trang_thai_label')}</th>
                    {canShowActionColumn && (
                      <th style={{ width: 140 }}>{st('thao_tac_column')}</th>
                    )}
                  </tr>
                }
              >
                {loading ? (
                  Array.from({ length: 3 }).map((_, row) => (
                    <tr key={row}>
                      {Array.from({ length: canShowActionColumn ? 9 : 8 }).map(
                        (__, col) => (
                          <td key={col}>
                            <div className="placeholder-glow">
                              <span className="placeholder col-8"></span>
                            </div>
                          </td>
                        ),
                      )}
                    </tr>
                  ))
                ) : ticketTypes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canShowActionColumn ? 9 : 8}
                      className="text-center text-muted py-5"
                    >
                      <div className="mb-2">
                        <i className="ri-price-tag-3-line fs-1 text-muted"></i>
                      </div>
                      {st('chua_co_loai_ve_nao_text')}
                    </td>
                  </tr>
                ) : (
                  ticketTypes.map(
                    (ticketType: TicketType, rowIndex: number) => (
                      <tr key={ticketType.id}>
                        <td className="text-center">
                          {getSttNumber(
                            pagination.currentPage,
                            pagination.pageSize,
                            rowIndex,
                          )}
                        </td>
                        <td className="fw-medium">{ticketType.code}</td>
                        <td>{ticketType.name}</td>
                        <td>{ticketType.digits}</td>
                        <td>{ticketType.drawRounds}</td>
                        <td>
                          {ticketType.quantityPerPeriod?.toLocaleString(
                            "vi-VN",
                          )}
                        </td>
                        <td>{ticketType.displayOrder}</td>
                        <td>
                          <span
                            className={`badge bg-${ticketType.isLocked ? "danger" : "success"}-subtle text-${ticketType.isLocked ? "danger" : "success"}`}
                          >
                            {ticketType.isLocked ? st('da_khoa_text') : st('dang_mo_text')}
                          </span>
                        </td>
                        {canShowActionColumn && (
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              {PageActionPermission.canDisplay(
                                location.pathname,
                                "button-edit",
                              ) && (
                                <Button
                                  color="secondary"
                                  outline
                                  size="sm"
                                  className="btn-icon"
                                  title={st('sua_title')}
                                  data-action-code="button-edit"
                                  data-action-name="Sửa loại vé"
                                  disabled={submitting}
                                  onClick={() => handleOpenEdit(ticketType)}
                                >
                                  <i className="ri-pencil-line"></i>
                                </Button>
                              )}
                              {PageActionPermission.canDisplay(
                                location.pathname,
                                "button-toggle-lock",
                              ) && (
                                <Button
                                  color={
                                    ticketType.isLocked ? "success" : "danger"
                                  }
                                  outline
                                  size="sm"
                                  className="btn-icon"
                                  title={
                                    ticketType.isLocked ? st('mo_khoa_text') : st('khoa_text')
                                  }
                                  data-action-code="button-toggle-lock"
                                  data-action-name="Khóa/Mở khóa loại vé"
                                  disabled={submitting}
                                  onClick={() => handleToggleLock(ticketType)}
                                >
                                  <i
                                    className={
                                      ticketType.isLocked
                                        ? "ri-lock-unlock-line"
                                        : "ri-lock-line"
                                    }
                                  ></i>
                                </Button>
                              )}
                              {PageActionPermission.canDisplay(
                                location.pathname,
                                "button-delete",
                              ) && (
                                <Button
                                  color="danger"
                                  outline
                                  size="sm"
                                  className="btn-icon"
                                  title={st('xoa_title')}
                                  data-action-code="button-delete"
                                  data-action-name="Xóa loại vé"
                                  disabled={submitting || deleting}
                                  onClick={() => setPendingDelete(ticketType)}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ),
                  )
                )}
              </DataTableShell>

              <Row className="mt-3 align-items-center">
                <Col md={6}>
                  <span className="text-muted">
                    {st('trang_text')} {pagination.currentPage}
                    {pagination.totalPages ? ` / ${pagination.totalPages}` : ""}
                  </span>
                </Col>
                <Col md={6}>
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      size="sm"
                      color="light"
                      disabled={pagination.currentPage <= 1}
                      onClick={() =>
                        dispatch(setPage(pagination.currentPage - 1))
                      }
                    >
                      {st('truoc_button')}
                    </Button>
                    <Button
                      size="sm"
                      color="light"
                      disabled={
                        pagination.totalPages > 0 &&
                        pagination.currentPage >= pagination.totalPages
                      }
                      onClick={() =>
                        dispatch(setPage(pagination.currentPage + 1))
                      }
                    >
                      {st('sau_button')}
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={modalState.create} toggle={handleCloseCreate} centered>
        <ModalHeader className="bg-light p-3" toggle={handleCloseCreate}>
          {st('them_loai_ve_button')}
        </ModalHeader>
        <Form onSubmit={handleCreateSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>
                {st('ten_loai_ve_column')} <span className="text-danger">*</span>
              </Label>
              <Input
                required
                placeholder={st('vd_mien_nam_5_so_placeholder')}
                value={createForm.name}
                data-action-code="create-input-name"
                data-action-name="Tên loại vé (Thêm mới)"
                disabled={
                  !PageActionPermission.canEnable(
                    location.pathname,
                    "create-input-name",
                  )
                }
                onChange={(event) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    name: event.target.value,
                  }))
                }
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {st('so_chu_so_dai_so_ve_label')} <span className="text-danger">*</span>
                  </Label>
                  <NumberInput
                    required
                    value={createForm.digits}
                    data-action-code="create-input-digits"
                    data-action-name="Số chữ số dải số vé (Thêm mới)"
                    disabled={
                      !PageActionPermission.canEnable(
                        location.pathname,
                        "create-input-digits",
                      )
                    }
                    onChange={(val) =>
                      setCreateForm((prev) => ({ ...prev, digits: val }))
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {st('so_lan_quay_thuong_column')} <span className="text-danger">*</span>
                  </Label>
                  <NumberInput
                    required
                    value={createForm.drawRounds}
                    data-action-code="create-input-draw-rounds"
                    data-action-name="Số lần quay thưởng (Thêm mới)"
                    disabled={
                      !PageActionPermission.canEnable(
                        location.pathname,
                        "create-input-draw-rounds",
                      )
                    }
                    onChange={(val) =>
                      setCreateForm((prev) => ({ ...prev, drawRounds: val }))
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {st('so_luong_ve_tao_moi_ky_column')}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <NumberInput
                    required
                    value={createForm.quantityPerPeriod}
                    data-action-code="create-input-quantity-per-period"
                    data-action-name="Số lượng vé tạo mỗi kỳ (Thêm mới)"
                    disabled={
                      !PageActionPermission.canEnable(
                        location.pathname,
                        "create-input-quantity-per-period",
                      )
                    }
                    onChange={(val) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        quantityPerPeriod: val,
                      }))
                    }
                  />
                  {createQuantityError && (
                    <FormFeedback type="invalid">
                      {createQuantityError}
                    </FormFeedback>
                  )}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>{st('thu_tu_hien_thi_column')}</Label>
                  <NumberInput
                    value={createForm.displayOrder}
                    data-action-code="create-input-display-order"
                    data-action-name="Thứ tự hiển thị (Thêm mới)"
                    disabled={
                      !PageActionPermission.canEnable(
                        location.pathname,
                        "create-input-display-order",
                      )
                    }
                    onChange={(val) =>
                      setCreateForm((prev) => ({ ...prev, displayOrder: val }))
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button
                color="light"
                onClick={handleCloseCreate}
                disabled={submitting}
              >
                {st('huy_button')}
              </Button>
              {PageActionPermission.canDisplay(
                location.pathname,
                "button-create-submit",
              ) && (
                <Button
                  color="success"
                  type="submit"
                  data-action-code="button-create-submit"
                  data-action-name="Tạo loại vé"
                  disabled={submitting}
                >
                  {submitting && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  {st('tao_loai_ve_button')}
                </Button>
              )}
            </div>
          </div>
        </Form>
      </Modal>

      <Modal isOpen={modalState.edit} toggle={handleCloseEdit} centered>
        <ModalHeader className="bg-light p-3" toggle={handleCloseEdit}>
          {st('sua_loai_ve_title')} {selectedTicketType?.code}
        </ModalHeader>
        <Form onSubmit={handleEditSubmit}>
          <ModalBody>
            {editForm && (
              <>
                <FormGroup>
                  <Label>
                    {st('ten_loai_ve_column')} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    required
                    value={editForm.name}
                    data-action-code="edit-input-name"
                    data-action-name="Tên loại vé (Sửa)"
                    disabled={
                      !PageActionPermission.canEnable(
                        location.pathname,
                        "edit-input-name",
                      )
                    }
                    onChange={(event) =>
                      setEditForm(
                        (prev) => prev && { ...prev, name: event.target.value },
                      )
                    }
                  />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        {st('so_chu_so_dai_so_ve_label')}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <NumberInput
                        required
                        value={editForm.digits}
                        data-action-code="edit-input-digits"
                        data-action-name="Số chữ số dải số vé (Sửa)"
                        disabled={
                          !PageActionPermission.canEnable(
                            location.pathname,
                            "edit-input-digits",
                          )
                        }
                        onChange={(val) =>
                          setEditForm(
                            (prev) => prev && { ...prev, digits: val },
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        {st('so_lan_quay_thuong_column')}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <NumberInput
                        required
                        value={editForm.drawRounds}
                        data-action-code="edit-input-draw-rounds"
                        data-action-name="Số lần quay thưởng (Sửa)"
                        disabled={
                          !PageActionPermission.canEnable(
                            location.pathname,
                            "edit-input-draw-rounds",
                          )
                        }
                        onChange={(val) =>
                          setEditForm(
                            (prev) => prev && { ...prev, drawRounds: val },
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label>
                        {st('so_luong_ve_tao_moi_ky_column')}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <NumberInput
                        required
                        value={editForm.quantityPerPeriod}
                        data-action-code="edit-input-quantity-per-period"
                        data-action-name="Số lượng vé tạo mỗi kỳ (Sửa)"
                        disabled={
                          !PageActionPermission.canEnable(
                            location.pathname,
                            "edit-input-quantity-per-period",
                          )
                        }
                        onChange={(val) =>
                          setEditForm(
                            (prev) =>
                              prev && { ...prev, quantityPerPeriod: val },
                          )
                        }
                      />
                      {editQuantityError && (
                        <FormFeedback type="invalid">
                          {editQuantityError}
                        </FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>{st('thu_tu_hien_thi_column')}</Label>
                      <NumberInput
                        value={editForm.displayOrder}
                        data-action-code="edit-input-display-order"
                        data-action-name="Thứ tự hiển thị (Sửa)"
                        disabled={
                          !PageActionPermission.canEnable(
                            location.pathname,
                            "edit-input-display-order",
                          )
                        }
                        onChange={(val) =>
                          setEditForm(
                            (prev) => prev && { ...prev, displayOrder: val },
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button
                color="light"
                onClick={handleCloseEdit}
                disabled={submitting}
              >
                {st('huy_button')}
              </Button>
              {PageActionPermission.canDisplay(
                location.pathname,
                "button-edit-submit",
              ) && (
                <Button
                  color="success"
                  type="submit"
                  data-action-code="button-edit-submit"
                  data-action-name="Lưu thay đổi loại vé"
                  disabled={submitting}
                >
                  {submitting && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  {st('luu_thay_doi_button')}
                </Button>
              )}
            </div>
          </div>
        </Form>
      </Modal>

      <Modal
        isOpen={!!pendingDelete}
        toggle={() => !deleting && setPendingDelete(null)}
        centered
      >
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger"></i>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{st('xoa_loai_ve_nay_title')}</h4>
              <p className="text-muted mx-4 mb-0">
                {st('ban_co_chac_chan_muon_xoa_text')}{" "}
                <b>
                  {pendingDelete?.name} ({pendingDelete?.code})
                </b>{" "}
                {st('khong_neu_loai_ve_dang_duoc_text')}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button
              color="light"
              className="w-sm"
              disabled={deleting}
              onClick={() => setPendingDelete(null)}
            >
              {st('dong_button')}
            </Button>
            <Button
              color="danger"
              className="w-sm"
              disabled={deleting}
              data-action-code="button-delete"
              data-action-name="Xác nhận xóa loại vé"
              onClick={handleConfirmDelete}
            >
              {deleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" />
                  {st('dang_xoa_text')}
                </>
              ) : (
                st('co_xoa_text')
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default TicketTypeManagementPage;
